import React from 'react'
import { Card, Group, Stack, Text, Title, Button, Divider, Box } from '@mantine/core'
import { IconCheck, IconCalendar, IconClock, IconUser, IconStethoscope } from '@tabler/icons-react'

export interface AppointmentSuccessCardProps {
  doctorName: string
  patientName: string
  dateLabel: string // e.g. "January 12, 2026"
  timeLabel: string // e.g. "13:30"
  onBookAnother?: () => void
  closeStepper: () => void
  isBookingAnotherAppointmentEnabled?: boolean
}

interface DetailRowProps {
  icon: React.ReactNode
  label: string
  value: string
}

export const AppointmentSuccessCard: React.FC<AppointmentSuccessCardProps> = ({
  doctorName,
  patientName,
  dateLabel,
  timeLabel,
  onBookAnother,
  closeStepper,
  isBookingAnotherAppointmentEnabled = true,
}) => {
  return (
    <Card
      radius="xl"
      p="xl"
      withBorder
      style={{
        width: 500,
        margin: '0 auto',
        borderColor: 'rgba(34, 139, 230, 0.18)',
        background:
          'radial-gradient(1200px 600px at 20% -10%, rgba(34,139,230,0.14) 0%, transparent 55%),' +
          'radial-gradient(900px 500px at 110% 10%, rgba(18,184,134,0.12) 0%, transparent 55%),' +
          'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.98) 100%)',
        boxShadow: '0 20px 45px rgba(0,0,0,0.08), 0 2px 10px rgba(0,0,0,0.05)',
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Group gap="sm">
          <Box
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(18, 184, 134, 0.12)',
              border: '1px solid rgba(18, 184, 134, 0.25)',
            }}
          >
            <IconCheck size={22} />
          </Box>

          <Stack gap={2}>
            <Title order={3} style={{ lineHeight: 1.1 }}>
              Appointment confirmed
            </Title>
          </Stack>
        </Group>
      </Group>

      <Divider my="lg" />

      <section className="grid grid-cols-2 gap-y-4">
        <DetailRow icon={<IconStethoscope size={30} />} label="Provider" value={doctorName} />

        <DetailRow icon={<IconUser size={30} />} label="Patient" value={patientName} />

        <DetailRow icon={<IconCalendar size={30} />} label="Date" value={dateLabel} />

        <DetailRow icon={<IconClock size={30} />} label="Time" value={timeLabel} />
      </section>

      <Divider my="lg" />

      {/* Actions */}
      <Group justify="space-between" align="center">
        {isBookingAnotherAppointmentEnabled && (
          <Button onClick={onBookAnother}>Book another appointment</Button>
        )}
        <Button onClick={closeStepper}>Close</Button>
      </Group>
    </Card>
  )
}
const DetailRow: React.FC<DetailRowProps> = ({ icon, label, value }) => {
  return (
    <Group align="flex-start" gap="sm" wrap="nowrap">
      <Box
        style={{
          width: 46,
          height: 46,
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          background: 'rgba(34, 139, 230, 0.10)',
          border: '1px solid rgba(34, 139, 230, 0.18)',
          flex: '0 0 auto',
        }}
      >
        {icon}
      </Box>

      <Stack gap={2} style={{ minWidth: 0 }}>
        <Text size="xs" c="dimmed" style={{ textAlign: 'left' }}>
          {label}
        </Text>
        <Text fw={600} style={{ wordBreak: 'break-word' }}>
          {value}
        </Text>
      </Stack>
    </Group>
  )
}
